/**
 * Faixas de CEP por UF (Brasil).
 */
const CEP_BR_RANGES = {
  Acre: [{ start: 69900000, end: 69999999 }],
  Alagoas: [{ start: 57000000, end: 57999999 }],
  Amapá: [{ start: 68900000, end: 68999999 }],
  Amazonas: [
    { start: 69000000, end: 69299999 },
    { start: 69400000, end: 69899999 },
  ],
  Bahia: [{ start: 40000000, end: 48999999 }],
  Ceará: [{ start: 60000000, end: 63999999 }],
  'Distrito Federal': [
    { start: 70000000, end: 72799999 },
    { start: 73000000, end: 73699999 },
  ],
  'Espírito Santo': [{ start: 29000000, end: 29999999 }],
  Goiás: [
    { start: 72800000, end: 72999999 },
    { start: 73700000, end: 76799999 },
  ],
  Maranhão: [{ start: 65000000, end: 65999999 }],
  'Mato Grosso': [{ start: 78000000, end: 78899999 }],
  'Mato Grosso do Sul': [{ start: 79000000, end: 79999999 }],
  'Minas Gerais': [{ start: 30000000, end: 39999999 }],
  Pará: [{ start: 66000000, end: 68899999 }],
  Paraíba: [{ start: 58000000, end: 58999999 }],
  Paraná: [{ start: 80000000, end: 87999999 }],
  Pernambuco: [{ start: 50000000, end: 56999999 }],
  Piauí: [{ start: 64000000, end: 64999999 }],
  'Rio de Janeiro': [{ start: 20000000, end: 28999999 }],
  'Rio Grande do Norte': [{ start: 59000000, end: 59999999 }],
  'Rio Grande do Sul': [{ start: 90000000, end: 99999999 }],
  Rondônia: [{ start: 76800000, end: 76999999 }],
  Roraima: [{ start: 69300000, end: 69399999 }],
  'Santa Catarina': [{ start: 88000000, end: 89999999 }],
  'São Paulo': [{ start: parseInt('01000000', 10), end: 19999999 }],
  Sergipe: [{ start: 49000000, end: 49999999 }],
  Tocantins: [{ start: 77000000, end: 77999999 }],
};

function getBrazilStateFromCep(zipRaw) {
  const cep = String(zipRaw).replace(/\D/g, '');
  if (cep.length !== 8) return '';
  const n = parseInt(cep, 10);
  for (const estado of Object.keys(CEP_BR_RANGES)) {
    for (const range of CEP_BR_RANGES[estado]) {
      if (n >= range.start && n <= range.end) return estado;
    }
  }
  return '';
}

function setProvinceSelectByValue(selectEl, value) {
  if (!selectEl || !value) return;
  Array.from(selectEl.options).forEach((option, index) => {
    if (option.value === value || option.innerHTML === value) {
      selectEl.selectedIndex = index;
    }
  });
}

function isBrazilCountry(value) {
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'brazil' || normalized === 'brasil' || normalized === 'br';
}

function formatBrazilCep(zip) {
  const digits = String(zip).replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : zip;
}

const BR_PROVINCE_CODES = {
  Acre: 'AC',
  Alagoas: 'AL',
  Amapá: 'AP',
  Amazonas: 'AM',
  Bahia: 'BA',
  Ceará: 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  Goiás: 'GO',
  Maranhão: 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  Pará: 'PA',
  Paraíba: 'PB',
  Paraná: 'PR',
  Pernambuco: 'PE',
  Piauí: 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  Rondônia: 'RO',
  Roraima: 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  Sergipe: 'SE',
  Tocantins: 'TO',
};

const STOREFRONT_API_URL = '/api/2026-07/graphql.json';

/**
 * Lê uma resposta GraphQL que pode vir em multipart/mixed (@defer),
 * mesclando os fragments incrementais no objeto `data`.
 */
async function readGraphqlResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const boundaryMatch = contentType.match(/boundary="?([^";]+)"?/);
  if (!contentType.includes('multipart') || !boundaryMatch) {
    return response.json();
  }

  const text = await response.text();
  const result = { data: {}, errors: [] };
  text.split(`--${boundaryMatch[1]}`).forEach((part) => {
    const jsonStart = part.indexOf('{');
    if (jsonStart === -1) return;
    let chunk;
    try {
      chunk = JSON.parse(part.slice(jsonStart));
    } catch (e) {
      return;
    }
    if (chunk.data) Object.assign(result.data, chunk.data);
    if (chunk.errors) result.errors.push(...chunk.errors);
    (chunk.incremental || []).forEach((inc) => {
      let target = result.data;
      (inc.path || []).forEach((key) => {
        target[key] = target[key] || {};
        target = target[key];
      });
      Object.assign(target, inc.data || {});
      if (inc.errors) result.errors.push(...inc.errors);
    });
  });
  return result;
}

async function storefrontRequest(query, variables) {
  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'multipart/mixed, application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);
  const result = await readGraphqlResponse(response);
  if (result.errors && result.errors.length) throw new Error(result.errors[0].message);
  return result.data;
}

/**
 * Calcula o frete de uma variante sem depender do carrinho da sessão:
 * cria um carrinho descartável pela Storefront API com o item e o CEP e lê
 * as opções de entrega (incluindo taxas de app/transportadora).
 */
async function fetchProductShippingRates({ variantId, zip, provinceCode }) {
  const createData = await storefrontRequest(
    `mutation ShippingEstimateCart($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id }
        userErrors { message }
      }
    }`,
    {
      input: {
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        buyerIdentity: { countryCode: 'BR' },
        delivery: {
          addresses: [
            {
              address: { deliveryAddress: { zip, provinceCode, countryCode: 'BR' } },
              selected: true,
              oneTimeUse: true,
            },
          ],
        },
      },
    }
  );

  const { cart, userErrors } = createData.cartCreate || {};
  if (!cart?.id) throw new Error(userErrors?.[0]?.message || 'cartCreate failed');

  const cartData = await storefrontRequest(
    `query ShippingEstimate($id: ID!) {
      cart(id: $id) {
        id
        ... DeliveryGroups @defer
      }
    }
    fragment DeliveryGroups on Cart {
      deliveryGroups(first: 5, withCarrierRates: true) {
        nodes {
          deliveryOptions {
            title
            handle
            description
            estimatedCost { amount currencyCode }
          }
        }
      }
    }`,
    { id: cart.id }
  );

  const groups = cartData.cart?.deliveryGroups?.nodes || [];
  const options = groups.flatMap((group) => group.deliveryOptions || []);
  return {
    shipping_rates: options.map((option) => ({
      name: option.title,
      code: option.handle,
      description: option.description || '',
      price: option.estimatedCost?.amount,
      currency: option.estimatedCost?.currencyCode,
    })),
  };
}

/**
 * Busca as taxas do carrinho da sessão pelo fluxo assíncrono do Shopify
 * (prepare + polling). Só funciona com itens no carrinho.
 */
async function fetchShippingRates(cartUrl, shippingAddress, { maxAttempts = 15, interval = 700 } = {}) {
  const params = new URLSearchParams();
  Object.entries(shippingAddress).forEach(([key, value]) => {
    params.append(`shipping_address[${key}]`, value);
  });
  const query = params.toString();

  const prepare = await fetch(`${cartUrl}/prepare_shipping_rates.json?${query}`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!prepare.ok) {
    return { ok: false, data: await prepare.json().catch(() => ({})) };
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(`${cartUrl}/async_shipping_rates.json?${query}`, {
      headers: { Accept: 'application/json' },
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) return { ok: false, data };
    // 202 / shipping_rates null = ainda calculando
    if (response.status === 200 && data.shipping_rates !== null && data.shipping_rates !== undefined) {
      return { ok: true, data };
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  return { ok: true, data: { shipping_rates: [] } };
}

function getShippingStrings() {
  const t = window.FoxTheme?.translations || {};
  return {
    singleRate: t.shipping_calculator_one_result || '1 shipping rate found',
    multipleRates: t.shipping_calculator_multiple_results || '{{ count }} shipping rates found',
    noRates: t.shipping_calculator_not_found || 'No shipping rates found',
    freeShipping: t.shipping_calculator_free || 'Free shipping',
    invalidZip: t.shipping_calculator_invalid_zip || 'ZIP/postal code must have 8 digits.',
    zipNotFound: t.shipping_calculator_zip_not_found || 'Invalid ZIP/postal code. Please check and try again.',
    oneDay: t.shipping_calculator_one_day || '1 business day',
    multipleDays: t.shipping_calculator_multiple_days || '{{ count }} business days',
    localDelivery: t.shipping_calculator_local_delivery || 'Local delivery',
    error: t.shipping_calculator_error || 'Unable to calculate shipping rates',
    enterZip: t.shipping_calculator_enter_zip || 'Please enter a postal code',
  };
}

if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', this.handleSubmit.bind(this));
    }

    connectedCallback() {
      queueMicrotask(() => {
        if (!this.initialised) this.init();
      });
    }

    init() {
      if (this.initialised) return;
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.zip = this.querySelector('.js-zip-input');
      this.rates = this.querySelector('.js-rates');
      this.errors = this.querySelector('.js-errors');

      this.hideRates();
      this.hideErrors();

      // Na PDP o frete é da variante selecionada.
      if (this.dataset.variantId) {
        const section = this.closest('.shopify-section') || document;
        section.addEventListener('variant:update', (event) => {
          const variantId = event.detail?.resource?.id;
          if (!variantId || String(variantId) === this.dataset.variantId) return;
          this.dataset.variantId = String(variantId);
          this.hideRates();
          this.hideErrors();
        });
      }

      if (!this.zip || !this.country) {
        this.initialised = true;
        return;
      }

      const syncProvinceFromCep = () => {
        if (!this.country || !isBrazilCountry(this.country.value) || !this.zip || !this.province) return;
        const digits = this.zip.value.replace(/\D/g, '');
        if (digits.length !== 8) return;
        const state = getBrazilStateFromCep(this.zip.value);
        if (state) setProvinceSelectByValue(this.province, state);
      };

      this.zip.addEventListener('blur', syncProvinceFromCep);
      let cepDebounce;
      this.zip.addEventListener('input', () => {
        clearTimeout(cepDebounce);
        this.hideErrors();
        this.hideRates();
        cepDebounce = setTimeout(syncProvinceFromCep, 400);
      });
      this.country.addEventListener('change', () => {
        if (isBrazilCountry(this.country.value)) syncProvinceFromCep();
      });

      this.initialised = true;
    }

    setLoading(isLoading) {
      if (!this.button) return;
      this.button.disabled = isLoading;
      this.button.classList.toggle('btn--loading', isLoading);
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    hideRates() {
      if (!this.rates) return;
      this.rates.hidden = true;
      this.rates.classList.add('hidden');
    }

    showRatesPanel() {
      if (!this.rates) return;
      this.rates.hidden = false;
      this.rates.classList.remove('hidden');
    }

    hideErrors() {
      if (!this.errors) return;
      this.errors.hidden = true;
      this.errors.classList.add('hidden');
      const textEl = this.errors.querySelector('.js-errors-text');
      if (textEl) textEl.innerHTML = '';
    }

    showErrorsPanel() {
      if (!this.errors) return;
      this.errors.hidden = false;
      this.errors.classList.remove('hidden');
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (!this.initialised) this.init();

      const strings = getShippingStrings();
      this.hideErrors();
      this.hideRates();
      this.setLoading(true);

      try {
        const zip = (this.zip?.value || '').trim();
        if (!zip) {
          this.showErrors({ zip: strings.enterZip });
          return;
        }

        if (isBrazilCountry(this.country?.value)) {
          const cepDigits = zip.replace(/\D/g, '');
          if (cepDigits.length !== 8) {
            this.showErrors({ zip: strings.invalidZip });
            return;
          }
          const stateFromCep = getBrazilStateFromCep(zip);
          if (!stateFromCep) {
            this.showErrors({ zip: strings.zipNotFound });
            return;
          }
          setProvinceSelectByValue(this.province, stateFromCep);
        }

        const shippingAddress = {
          zip: isBrazilCountry(this.country?.value) ? formatBrazilCep(zip) : zip,
          country: this.country?.value || 'Brazil',
          province: this.province?.value || '',
        };

        const cartUrl = window.FoxTheme?.routes?.cart_url || '/cart';
        let result = null;
        const provinceCode =
          BR_PROVINCE_CODES[shippingAddress.province] || BR_PROVINCE_CODES[getBrazilStateFromCep(zip)];
        if (this.dataset.variantId && isBrazilCountry(shippingAddress.country) && provinceCode) {
          try {
            result = {
              ok: true,
              data: await fetchProductShippingRates({
                variantId: this.dataset.variantId,
                zip: shippingAddress.zip,
                provinceCode,
              }),
            };
          } catch (error) {
            console.warn('Storefront shipping estimate failed, falling back to cart:', error);
          }
        }
        if (!result) result = await fetchShippingRates(cartUrl, shippingAddress);
        const { ok, data } = result;

        if (ok && data.shipping_rates) {
          this.showRates(data);
        } else if (ok) {
          this.showErrors({ notFound: strings.noRates });
        } else {
          this.showErrors(data);
        }
      } catch (error) {
        this.showErrors({ error: strings.error });
      } finally {
        this.setLoading(false);
      }
    }

    showRates(data) {
      const strings = getShippingStrings();
      const headingEl = this.rates.querySelector('.js-rates-heading');
      const textEl = this.rates.querySelector('.js-rates-text');
      textEl.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const count = data.shipping_rates.length;
        let rates = '';

        data.shipping_rates.forEach((rate) => {
          let formattedRate;
          if (Number(rate.price) > 0) {
            formattedRate =
              rate.currency === 'BRL'
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rate.price)
                : new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: rate.currency || 'USD',
                  }).format(rate.price);
          } else {
            formattedRate = strings.freeShipping;
          }

          let deliveryInfo = '';

          if (rate.name === 'Local Delivery' || rate.code === 'Local Delivery') {
            deliveryInfo = rate.description || '';
          } else if (Array.isArray(rate.delivery_days) && rate.delivery_days.length > 0) {
            deliveryInfo =
              rate.delivery_days[0] === 1
                ? strings.oneDay
                : strings.multipleDays.replace('{{ count }}', rate.delivery_days[0]);
          } else if (rate.description) {
            // Storefront API (PDP) não retorna delivery_days; apps de frete enviam o prazo na descrição.
            deliveryInfo = rate.description;
          }

          const rateName =
            rate.name === 'Local Delivery' ? strings.localDelivery : rate.name;

          rates += `<li><p>${rateName}: <b>${formattedRate}</b></p>${
            deliveryInfo ? `<p>${deliveryInfo}</p>` : ''
          }</li>`;
        });

        headingEl.hidden = false;
        headingEl.textContent =
          count === 1
            ? strings.singleRate
            : strings.multipleRates.replace('{{ count }}', count);
        textEl.innerHTML = `<ul class="styled-list">${rates}</ul>`;
      } else {
        headingEl.hidden = true;
        textEl.innerHTML = strings.noRates;
      }

      this.showRatesPanel();
    }

    showErrors(data) {
      const strings = getShippingStrings();
      let errors = '';

      Object.keys(data || {}).forEach((key) => {
        const value = data[key];
        if (Array.isArray(value)) {
          value.forEach((msg) => {
            errors += `<li>${msg}</li>`;
          });
        } else if (value) {
          errors += `<li>${value}</li>`;
        }
      });

      if (!errors) {
        errors = `<li>${strings.error}</li>`;
      }

      this.errors.querySelector('.js-errors-text').innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.showErrorsPanel();
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
